import { employerVerificationDashboardHttp } from './employerVerificationDashboardClient';

const DASH = '/api/employer-verification/v1/admin/dashboard';
const VERIF = '/api/employer-verification/v1/verifications';
const ADMIN_PLACES_SESSION_KEY = 'ev-admin-places-session-id';

function getAdminPlacesSessionId(): string {
  if (typeof window === 'undefined') {
    return `admin-${Date.now()}`;
  }

  const existing = window.sessionStorage.getItem(ADMIN_PLACES_SESSION_KEY);
  if (existing?.trim()) {
    return existing.trim();
  }

  const next =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `admin-${Date.now()}-${Math.floor(Math.random() * 1e6)}`;
  window.sessionStorage.setItem(ADMIN_PLACES_SESSION_KEY, next);
  return next;
}

export function resetAdminPlacesSessionId(): void {
  if (typeof window === 'undefined') {
    return;
  }
  window.sessionStorage.removeItem(ADMIN_PLACES_SESSION_KEY);
}

export type GateFlowStep = {
  gateKey: string;
  shortCode: string;
  title: string;
  whatHappens: string;
  outcomesExplained: string;
};

export type DashboardOutcomeTile = {
  id: string;
  finalStatus: string | null;
  title: string;
  subtitle: string;
  presentation: 'success' | 'error' | 'warning' | 'neutral';
  count: number;
};

export type DashboardListControls = {
  sortOptions: Array<{ value: string; label: string; description: string }>;
  gateIssueFilterOptions: Array<{
    value: '' | 'ANY_FAIL' | 'ANY_REVIEW';
    label: string;
    description: string;
  }>;
  outcomeQuickFilters: Array<{ finalStatus: string | null; label: string }>;
};

export type EmployerAutocompleteSuggestionDto = {
  placeId: string;
  mainText: string;
  secondaryText: string;
  fullText: string;
  types: string[];
};

export type EmployerFootprintPurchasePinDto = {
  merchantName: string;
  address: string | null;
  /** Null when AI omitted coords — admin geocodes from `address`. */
  latitude: number | null;
  longitude: number | null;
  category: string | null;
};

export type EmployerFootprintEmployerPinDto = {
  name: string;
  address: string | null;
  /** Null when AI omitted coords — admin geocodes from `address`. */
  latitude: number | null;
  longitude: number | null;
  distanceKm: number | null;
};

export type EmployerFootprintLocationEvidenceDto = {
  geographicCluster: string | null;
  purchasesAnalyzed: number;
  purchasesWithCoordinates: EmployerFootprintPurchasePinDto[];
  possibleEmployer: EmployerFootprintEmployerPinDto | null;
  locationReasoning: string | null;
};

export type EmployerFootprintAdminEntityProfileDto = {
  incorporationDate: string | null;
  incorporationJurisdiction: string | null;
  entityStatus: string | null;
  entityType: string | null;
  organizationCategory: string | null;
  ownershipCountry: string | null;
  ultimateParentName: string | null;
  publiclyTraded: boolean | null;
  stockTickerExchange: string | null;
  naics: string | null;
  naicsInferred: boolean | null;
  amalgamationNote: string | null;
  registryRecordId: string | null;
  registrySourceUrl: string | null;
};

export type EmployerFootprintAdminExecutivesDto = {
  globalCeo: string | null;
  canadaPresident: string | null;
  canadaCfo: string | null;
};

export type EmployerFootprintAdminBreakdownDto = {
  extractionConfidence: number | null;
  webExistenceConfidence: number | null;
  locationCorrelationScore: number | null;
};

export type EmployerFootprintCompanyProfileDto = {
  officialWebsite: string | null;
  linkedInUrl: string | null;
  businessNumber: string | null;
  corporationId: string | null;
  sector: string | null;
  companyOriginCountry: string | null;
  founders?: string[];
  executives?: EmployerFootprintAdminExecutivesDto | null;
  entityProfile?: EmployerFootprintAdminEntityProfileDto | null;
  reasoningSummary?: string | null;
  stringMatchReasoning?: string | null;
  confidencePenalties?: string[];
  breakdown?: EmployerFootprintAdminBreakdownDto | null;
};

export type EmployerFootprintAdminViewDto = {
  applicationId: string;
  analysisId: string | null;
  status: 'NOT_STARTED' | 'PENDING' | 'RUNNING' | 'READY' | 'FAILED' | string;
  journeyHint: 'POLL' | 'CONFIRM' | 'PAYSTUBS' | string;
  paystubsRequiredReason: string | null;
  confidenceThreshold: number;
  overallConfidence: number | null;
  meetsConfidenceThreshold: boolean;
  detectedEmployer: string | null;
  /** AI digital footprint matched branch address (display only unless declared address is absent). */
  address: string | null;
  /** Employer address from the application / verification request (preferred for map geocode). */
  declaredEmployerAddress?: string | null;
  phone: string | null;
  candidateCount: number;
  statusReason: string | null;
  /** Operator copy from EV (persisted at analyze when available). */
  adminMessage?: string | null;
  customerMessageKey?: string | null;
  customerMessage?: string | null;
  updatedAt: string | null;
  locationEvidence: EmployerFootprintLocationEvidenceDto | null;
  companyProfile?: EmployerFootprintCompanyProfileDto | null;
};

export type EmployerPlaceDetailsDto = {
  placeId: string;
  name: string;
  formattedAddress: string;
  phoneNumber: string | null;
  businessStatus: string | null;
  website: string | null;
  latitude: number;
  longitude: number;
  types: string[];
};

export type EmployerVerificationDashboardSummary = {
  totals: Record<string, number>;
  hints: { passed: number; failed: number; manualReview: number; unknown: number };
  outcomeTiles: DashboardOutcomeTile[];
  listControls: DashboardListControls;
  gateFlowSteps: GateFlowStep[];
  pipelineStatusLegend: Record<string, { label: string; operatorMeaning: string }>;
  finalDecisionLegend: Record<string, string>;
};

export type PersistedGateCheckpointDto = {
  step: string;
  status: string;
  /** Pre-soften evaluator outcome when persisted by employer-verification. */
  evaluationStatus?: string;
  reason: string;
  isHardStop: boolean;
};

export type HoverPayload = {
  traceSummaryLine: string;
  checkpointsShort: Array<{
    gateKey: string;
    shortCode: string;
    title: string;
    status: string;
  }>;
  failReasonPreview: string | null;
  firstFailedCheckpoint?: {
    gate: string;
    gateTitle: string;
    step: string;
    stepLabel: string;
    reason: string;
  } | null;
  confidenceScore: number | null;
};

export type EmployerVerificationListItem = {
  id: string;
  applicationId: string;
  applicantId: string;
  ccid: string | null;
  entityId: string | null;
  finalStatus: string | null;
  confidenceScore: number | null;
  failReason: string | null;
  attemptNumber: number | null;
  verifiedAt: string | null;
  gates: Record<string, string | null>;
  linkageMode: string | null;
  gateMessages: Record<string, string | null>;
  employer: { legalName: string | null; ceid: string | null; sector: string | null };
  loanApplication: {
    applicantEmail: string | null;
    status: string | null;
    productType: string | null;
    lastAction: string | null;
  };
  traceSummaryLine?: string;
  hoverPayload?: HoverPayload;
  gateCheckpointAudit?: Record<string, PersistedGateCheckpointDto[]> | null;
};

export type EmployerVerificationCheckpoint = {
  gate: string;
  statusLabel: string;
  adminSummary: string;
};

export type EmployerVerificationDetail = EmployerVerificationListItem & {
  checkpoints: EmployerVerificationCheckpoint[];
  reviewSignals: Record<string, unknown> | null;
  evidence: Record<string, unknown> | null;
  fraudSignals: Array<{
    id: string;
    signalName: string;
    riskContribution: number;
    observationData: Record<string, unknown>;
    observedAt: string | null;
  }>;
  gateFlowSteps?: GateFlowStep[];
  pipelineStatusLegend?: Record<string, { label: string; operatorMeaning: string }>;
  finalDecisionLegend?: Record<string, string>;
};

export type GateIssueFilter = '' | 'ANY_FAIL' | 'ANY_REVIEW';
export type DashboardGateKey =
  | 'EXISTENCE'
  | 'LINKAGE'
  | 'LEGITIMACY'
  | 'STRUCTURAL_SANITY'
  | 'TEMPORAL';
export type DashboardGateOutcome = 'PASS' | 'FAIL' | 'REVIEW';

export type GateOutcomeCountsResponse = {
  gates: Record<DashboardGateKey, Record<DashboardGateOutcome, number>>;
};

export type GateOutcomeApplicantsResponse = {
  total: number;
  limit: number;
  offset: number;
  gateKey: DashboardGateKey;
  outcome: DashboardGateOutcome;
  items: Array<{
    id: string;
    applicationId: string;
    applicantId: string;
    ccid: string | null;
    employerLegalName: string | null;
    gateStatus: string;
    gateReason: string | null;
    verifiedAt: string | null;
  }>;
};

export type CompanyDossier = {
  verification: {
    id: string;
    applicationId: string;
    applicantId: string;
    ccid: string | null;
    finalStatus: string | null;
    verifiedAt: string | null;
    attemptNumber: number | null;
  };
  requestSnapshot: {
    jobTitle: string | null;
    declaredStartDate: string | null;
    employerAddressNormalized: string | null;
    employerPhoneNormalized: string | null;
    employerPhoneVerified?: boolean;
  } | null;
  employer: {
    id: string;
    ceid: string;
    legalName: string;
    normalizedLegalName: string;
    sector: string | null;
    registryId: string | null;
    registryStatus: string | null;
    registrySource: string | null;
    registryConfidence: number | null;
    website: string | null;
    isActive: boolean | null;
  } | null;
  intelligenceSnapshot: Record<string, unknown> | null;
  verificationAddress: {
    normalizedKey: string | null;
    resolution: string;
    formattedAddress: string | null;
    latitude: number | null;
    longitude: number | null;
    branchType: string | null;
    locationId: string | null;
    currentVersion: Record<string, unknown> | null;
  };
  caches: {
    geocode: Record<string, unknown> | null;
    googlePlace: Record<string, unknown> | null;
  };
  map: {
    markers: Array<{ lat: number; lng: number; label: string }>;
    markerPolicy: string;
  };
  uiMeta: { refreshStrategy: string };
};

export const employerVerificationDashboardApi = {
  async gateReference(): Promise<
    Pick<
      EmployerVerificationDashboardSummary,
      'gateFlowSteps' | 'pipelineStatusLegend' | 'finalDecisionLegend'
    >
  > {
    const res = await employerVerificationDashboardHttp.get(
      `${DASH}/gate-reference`,
    );
    return res.data;
  },

  async summary(): Promise<EmployerVerificationDashboardSummary> {
    const res = await employerVerificationDashboardHttp.get<EmployerVerificationDashboardSummary>(
      `${DASH}/summary`,
    );
    return res.data;
  },

  async list(params: {
    limit?: number;
    offset?: number;
    latestPerApplicationOnly?: boolean;
    finalStatus?: string;
    applicationIdContains?: string;
    applicantIdContains?: string;
    employerNameContains?: string;
    ccidContains?: string;
    verifiedFrom?: string;
    verifiedTo?: string;
    gateIssue?: GateIssueFilter;
    sortBy?: string;
  }): Promise<{ total: number; limit: number; offset: number; items: EmployerVerificationListItem[] }> {
    const res = await employerVerificationDashboardHttp.get(`${DASH}/records`, {
      params,
    });
    return res.data;
  },

  async detail(id: string): Promise<EmployerVerificationDetail> {
    const res = await employerVerificationDashboardHttp.get<EmployerVerificationDetail>(
      `${DASH}/records/${encodeURIComponent(id)}`,
    );
    return res.data;
  },

  async employerFootprint(applicationId: string): Promise<EmployerFootprintAdminViewDto> {
    const res = await employerVerificationDashboardHttp.get<EmployerFootprintAdminViewDto>(
      `${DASH}/applications/${encodeURIComponent(applicationId)}/employer-footprint`,
    );
    return res.data;
  },

  async gateOutcomeCounts(params: {
    latestPerApplicationOnly?: boolean;
    finalStatus?: string;
    applicationIdContains?: string;
    applicantIdContains?: string;
    employerNameContains?: string;
    ccidContains?: string;
    verifiedFrom?: string;
    verifiedTo?: string;
    gateIssue?: GateIssueFilter;
  }): Promise<GateOutcomeCountsResponse> {
    const res = await employerVerificationDashboardHttp.get<GateOutcomeCountsResponse>(
      `${DASH}/gate-outcomes`,
      { params },
    );
    return res.data;
  },

  async gateOutcomeApplicants(params: {
    gateKey: DashboardGateKey;
    outcome: DashboardGateOutcome;
    limit?: number;
    offset?: number;
    latestPerApplicationOnly?: boolean;
    finalStatus?: string;
    applicationIdContains?: string;
    applicantIdContains?: string;
    employerNameContains?: string;
    ccidContains?: string;
    verifiedFrom?: string;
    verifiedTo?: string;
    gateIssue?: GateIssueFilter;
  }): Promise<GateOutcomeApplicantsResponse> {
    const res = await employerVerificationDashboardHttp.get<GateOutcomeApplicantsResponse>(
      `${DASH}/gate-applicants`,
      { params },
    );
    return res.data;
  },

  async companyDossier(verificationId: string): Promise<CompanyDossier> {
    const res = await employerVerificationDashboardHttp.get<CompanyDossier>(
      `${DASH}/company-dossier/${encodeURIComponent(verificationId)}`,
    );
    return res.data;
  },

  async autocomplete(q: string): Promise<EmployerAutocompleteSuggestionDto[]> {
    const res = await employerVerificationDashboardHttp.get<EmployerAutocompleteSuggestionDto[]>(
      `${VERIF}/places/autocomplete`,
      { params: { q, sessionId: getAdminPlacesSessionId() } },
    );
    return res.data;
  },

  async placeDetails(placeId: string): Promise<EmployerPlaceDetailsDto> {
    const sessionId = getAdminPlacesSessionId();
    const res = await employerVerificationDashboardHttp.get<EmployerPlaceDetailsDto>(
      `${VERIF}/places/${encodeURIComponent(placeId)}`,
      { params: { sessionId } },
    );
    resetAdminPlacesSessionId();
    return res.data;
  },
};
